// Animated per-avatar "swirl" background: solid palette color with flowing
// white wave lines, rendered once per palette into an offscreen canvas and
// copied into every visible `.avatar-swirl canvas.swirl-cv` each frame.

export interface SwirlPalette {
  name: string;
  color: string;
}

export const SWIRL_PALETTES: SwirlPalette[] = [
  { name: 'purple', color: '#8b5cf6' },
  { name: 'red', color: '#ef4444' },
  { name: 'blue', color: '#3b82f6' },
  { name: 'green', color: '#22c55e' },
  { name: 'orange', color: '#f97316' },
  { name: 'pink', color: '#ec4899' },
  { name: 'cyan', color: '#06b6d4' },
  { name: 'yellow', color: '#eab308' },
  { name: 'lime', color: '#84cc16' },
  { name: 'rose', color: '#f43f5e' },
  { name: 'indigo', color: '#6366f1' },
  { name: 'teal', color: '#14b8a6' },
  { name: 'amber', color: '#f59e0b' },
  { name: 'fuchsia', color: '#d946ef' },
  { name: 'sky', color: '#0ea5e9' },
];

const SWIRL_RES = 200;
const WAVE_COUNT = 7; // waves per palette
const WAVE_SPEED = 0.4; // flow speed
// Wave line opacities (white waves over solid bg)
const WAVE_ALPHAS = [0.35, 0.55, 0.25, 0.65, 0.4, 0.5, 0.3];

interface Wave {
  yBase: number;
  amplitude: number;
  frequency: number;
  phase: number;
  speed: number;
  strokeWidth: number;
  dashLen: number;
  gapLen: number;
  opacity: number;
}

let swirlRunning = false;
let swirlTime = 0;
let swirlLastFrame = -Infinity;
const swirlReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const swirlVisibility: IntersectionObserver | null =
  'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          (entry.target as HTMLElement).dataset.visible = entry.isIntersecting ? '1' : '0';
        });
      })
    : null;

// Per-palette offscreen canvases
const swirlOCs: Record<string, { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D }> = {};
// Wave definitions (shared geometry, palette supplies colors)
let waves: Wave[] = [];
// Cached swirl canvas references — updated by MutationObserver, not queried per frame
let swirlCanvases: HTMLCanvasElement[] = [];

function currentPalette(): SwirlPalette {
  return window._swirlPalette;
}

function initWaves(): void {
  waves = [];
  for (let i = 0; i < WAVE_COUNT; i++) {
    waves.push({
      yBase: (i + 0.5) / WAVE_COUNT,
      amplitude: 0.06 + Math.random() * 0.1,
      frequency: 1.5 + Math.random() * 2.0,
      phase: Math.random() * Math.PI * 2,
      speed: (0.6 + Math.random() * 0.8) * (Math.random() < 0.5 ? 1 : -1),
      strokeWidth: 2.0 + Math.random() * 2.5,
      dashLen: 6 + Math.random() * 14,
      gapLen: 3 + Math.random() * 8,
      opacity: 0.4 + Math.random() * 0.6, // vary brightness per wave
    });
  }
}

function getOC(paletteName: string) {
  if (!swirlOCs[paletteName]) {
    const c = document.createElement('canvas');
    c.width = SWIRL_RES;
    c.height = SWIRL_RES;
    swirlOCs[paletteName] = { canvas: c, ctx: c.getContext('2d')! };
  }
  return swirlOCs[paletteName];
}

function refreshSwirlCache(): void {
  swirlCanvases = Array.from(document.querySelectorAll<HTMLCanvasElement>('.avatar-swirl canvas.swirl-cv'));
}

function swirlTick(timestamp: number = performance.now()): void {
  if (!swirlRunning) return;
  if (document.hidden) {
    swirlRunning = false;
    return;
  }
  if (!swirlReducedMotion && timestamp - swirlLastFrame < 33) {
    requestAnimationFrame(swirlTick);
    return;
  }
  swirlLastFrame = timestamp;
  swirlTime += 0.033;
  const w = SWIRL_RES;
  const h = SWIRL_RES;

  // Find active palettes from cached canvas list
  const activePalettes = new Set<string>();
  for (const c of swirlCanvases) {
    if (!c.isConnected || c.dataset.visible === '0') continue;
    activePalettes.add((c.parentElement as HTMLElement)?.dataset.swirl || currentPalette().name);
  }

  // Render each active palette's offscreen canvas
  for (const pName of activePalettes) {
    const palette = SWIRL_PALETTES.find((p) => p.name === pName) || SWIRL_PALETTES[0];
    const oc = getOC(pName);
    const ctx = oc.ctx;

    // Solid background color
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = palette.color;
    ctx.fillRect(0, 0, w, h);

    // White flowing waves on top
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    for (let wi = 0; wi < waves.length; wi++) {
      const wave = waves[wi];
      const t = swirlTime * wave.speed * WAVE_SPEED;
      ctx.strokeStyle = `rgba(255,255,255,${WAVE_ALPHAS[wi % WAVE_ALPHAS.length]})`;
      ctx.lineWidth = wave.strokeWidth;
      ctx.setLineDash([wave.dashLen, wave.gapLen]);
      ctx.lineDashOffset = -t * 60;

      ctx.beginPath();
      const steps = 60;
      for (let s = 0; s <= steps; s++) {
        const px = (s / steps) * w;
        const xNorm = s / steps;
        const py = wave.yBase * h + Math.sin(xNorm * wave.frequency * Math.PI * 2 + wave.phase + t * 2) * wave.amplitude * h;
        if (s === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.stroke();
    }
    ctx.setLineDash([]);
  }

  // Copy to matching swirl canvases (from cache)
  for (const c of swirlCanvases) {
    if (!c.isConnected || c.dataset.visible === '0') continue;
    const pName = (c.parentElement as HTMLElement)?.dataset.swirl || currentPalette().name;
    const oc = swirlOCs[pName];
    if (oc) {
      const dc = c.getContext('2d')!;
      dc.clearRect(0, 0, c.width, c.height);
      dc.drawImage(oc.canvas, 0, 0, c.width, c.height);
    }
  }

  if (swirlReducedMotion) swirlRunning = false;
  else requestAnimationFrame(swirlTick);
}

function startSwirl(): void {
  if (swirlRunning) return;
  if (!waves.length) initWaves();
  swirlRunning = true;
  requestAnimationFrame(swirlTick);
}

function initSwirls(root: Document | Element = document): void {
  const elements: Element[] = [];
  if (root instanceof Element && root.matches('.avatar-swirl')) elements.push(root);
  if (root.querySelectorAll) elements.push(...Array.from(root.querySelectorAll('.avatar-swirl')));
  elements.forEach((el) => {
    if (el.querySelector('canvas.swirl-cv')) return;
    const cv = document.createElement('canvas');
    cv.className = 'swirl-cv';
    cv.width = SWIRL_RES;
    cv.height = SWIRL_RES;
    el.insertBefore(cv, el.firstChild);
    swirlVisibility?.observe(cv);
  });
  startSwirl();
}

document.addEventListener('visibilitychange', () => {
  if (!document.hidden) startSwirl();
});

function applySwirlPalette(p: SwirlPalette): void {
  window._swirlPalette = p;
}

function renderColorPicker(): void {
  const row = document.getElementById('color-picker');
  if (!row) return;
  row.innerHTML = SWIRL_PALETTES.map((p, i) => {
    return `<div class="color-swatch ${p.name === currentPalette().name ? 'active' : ''}" style="background:${p.color}" onclick="pickSwirlColor(${i})"></div>`;
  }).join('');
}

function pickSwirlColor(idx: number): void {
  applySwirlPalette(SWIRL_PALETTES[idx]);
  localStorage.setItem('wa_swirl', SWIRL_PALETTES[idx].name);
  document.querySelectorAll('.color-swatch').forEach((s, i) => s.classList.toggle('active', i === idx));
}

function loadSwirlColor(): void {
  const saved = localStorage.getItem('wa_swirl');
  const found = saved && SWIRL_PALETTES.find((c) => c.name === saved);
  applySwirlPalette(found || SWIRL_PALETTES[0]);
}

declare global {
  interface Window {
    SWIRL_PALETTES: SwirlPalette[];
    _swirlPalette: SwirlPalette;
    initSwirls: typeof initSwirls;
    applySwirlPalette: typeof applySwirlPalette;
    renderColorPicker: typeof renderColorPicker;
    pickSwirlColor: typeof pickSwirlColor;
    loadSwirlColor: typeof loadSwirlColor;
    _refreshSwirlCache: typeof refreshSwirlCache;
  }
}

// Exposed on window: the legacy inline script (index.html) reads/writes
// `_swirlPalette`/`SWIRL_PALETTES` as bare globals and calls these functions
// directly (e.g. `onclick="pickSwirlColor(2)"`, `initSwirls(node)`).
window.SWIRL_PALETTES = SWIRL_PALETTES;
window._swirlPalette = SWIRL_PALETTES[0];
window.initSwirls = initSwirls;
window.applySwirlPalette = applySwirlPalette;
window.renderColorPicker = renderColorPicker;
window.pickSwirlColor = pickSwirlColor;
window.loadSwirlColor = loadSwirlColor;
window._refreshSwirlCache = refreshSwirlCache;
